import * as moment from 'moment'
import * as React from 'react'
import 'react-dates/initialize'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, GlobalModal } from '../../../atoms'
import { ReservationMultiDatePicker } from 'components/pages/reservations/parts/atoms/ReservationMultiDatePicker'
import { getAges, ISelectedGuests } from 'utils/reservation'
import {
  CALENDAR_EVENTS_DEFAULT_MONTHS,
  CALENDAR_EVENTS_INITIAL_MONTHS,
  useCalendarEvents,
} from 'utils/useCalendarEvents'
import { getDatesFromSearchFilters, getSearchFiltersCookie } from 'utils/searchFiltersCookie'
import { buildReservationUrl } from 'utils/url'
import SvgIcon from 'components/atoms/SvgIcon'
import { setReservationStorageData } from 'utils/reservationSessionStorage'

interface IWindow extends Window {
  localePath: { [key: string]: string }
  location: any
  globalModal: GlobalModal
}
declare var window: IWindow

interface IProps {
  editable: boolean
  isOwner: boolean
  isSignedIn: boolean
  post_owner: interfaces.IUser
  post: any
  identification_required: boolean
  profile_required: boolean
  selectedDate: moment.Moment | null
  totalPrice: null | number
  user: interfaces.IUser
  setTotalPrice(price: number | null): void
  setSelectedDate(selectedDate: moment.Moment | null): void
}

export const PostStickyBox: React.FC<IProps> = props => {
  const searchFiltersCookie = React.useMemo(() => getSearchFiltersCookie(), [])

  // 詳細ページではモバイル版と同様に、日付・人数を検索Cookieから初期化する。
  // 過去の予約条件が混ざらないよう、同じツアーでもsessionStorageからは復元しない。
  // Cookieがなければ日付未選択・大人1名とし、詳細ページで選び直した値を優先する。
  // Check Availability押下時に現在値でsessionStorageを上書きし、遷移先の予約画面では
  // 引き続きsessionStorageを優先して予約条件を引き継ぐ。
  const [dates, setDates] = React.useState<moment.Moment[]>(() =>
    getDatesFromSearchFilters(searchFiltersCookie)
  )
  const [guests, setGuests] = React.useState<ISelectedGuests>(
    searchFiltersCookie?.guests ?? constants.DEFAULT_SEARCH_FILTER_GUESTS
  )
  const { post } = props

  const initDate = React.useMemo(() => {
    return getDatesFromSearchFilters(searchFiltersCookie)[0] || moment()
  }, [searchFiltersCookie])

  const getCurrentUrlParams = () => {
    return window.location.search
  }

  // Helper function to build URL with parameters
  const buildUrlWithParams = (baseUrl: string) => {
    const currentParams = getCurrentUrlParams()
    return currentParams ? `${baseUrl}${currentParams}` : baseUrl
  }

  const { calendarEvents, getCalendarEvents } = useCalendarEvents()

  React.useEffect(() => {
    getCalendarEvents(props.post.id, initDate, CALENDAR_EVENTS_INITIAL_MONTHS)
  }, [])
  const guestCount = React.useMemo(() => guests.adults + guests.children, [
    guests.adults,
    guests.children,
  ])

  const maxCount = React.useMemo(() => {
    return Number(post.booking_max_people)
  }, [post.booking_max_people])

  const isExceedingMaxTravelers = React.useMemo(() => {
    if (post.family_owner) {
      return false
    }

    return guestCount > maxCount
  }, [guestCount, maxCount, post.family_owner])

  React.useEffect(() => {
    getCalendarEvents(props.post.id, initDate, CALENDAR_EVENTS_INITIAL_MONTHS)
  }, [])

  const getCalendarEventsInReservationDatePicker = async (newDate: moment.Moment) => {
    await getCalendarEvents(props.post.id, newDate, CALENDAR_EVENTS_DEFAULT_MONTHS)
  }

  const handleCheckAvailability = () => {
    setReservationStorageData({
      initParams: {
        dates: dates.map(date => date.toISOString()),
        guests,
      },
    })

    window.location.href = buildReservationUrl(post, dates, guestCount, guests)
  }

  return (
    <StickyBox>
      <div className="PostShow_StickyBox_content">
        <div className="PostTop_buyButton_price">
          <span className="label">From</span>¥<span className="value">{post.price}</span>/
          <span className="unit">{post.post_type === 'group' ? 'Person' : 'Group'}</span>
        </div>
        {props.editable ? (
          <Button
            small={true}
            textColor={constants.COLORS.primary}
            borderColor={constants.COLORS.primary}
          >
            <a href={buildUrlWithParams(`/agent/posts/${props.post.slug}/edit`)}>
              <SvgIcon name="edit" className="icon" ariaHidden={true} role="presentation" />
              {I18n.t('generic.edit')}
            </a>
          </Button>
        ) : (
          <div>
            {post.last_booked_hours !== null && (
              <div className="LastBookedHours">
                <span className="label">
                  <SvgIcon name="history" className="icon" ariaHidden={true} role="presentation" />
                  {I18n.t('post.last_booked_hours_label')}
                </span>
                <span className="value">
                  {I18n.t('post.last_booked_hours', { hours: post.last_booked_hours })}
                </span>
                {I18n.t('post.last_booked_hours_unit')}
              </div>
            )}
            <div className="PostShow_DatePicker">
              <ReservationMultiDatePicker
                post={post}
                initialDate={initDate}
                dates={dates}
                setDates={setDates}
                guests={guests}
                guestCount={guestCount}
                setGuests={setGuests}
                calendarEvents={calendarEvents}
                getCalendarEvents={getCalendarEventsInReservationDatePicker}
                isPrivate={props.post.post_type === 'private'}
                ages={getAges(props.post.post_prices)}
              />
            </div>
            <p className="PostShow_PrivateGroupOption">
              If you want to book just for you and your group,
              <a
                href={constants.DMC_LINK + '/contact'}
                className="link_button"
                target="_blank"
                rel="noreferrer"
              >
                contact us
              </a>
              .
            </p>
            <div className="PostShow_StickyBox_buyButton">
              <Button
                largeAlt={true}
                primary={true}
                handleClick={handleCheckAvailability}
                disabled={isExceedingMaxTravelers}
              >
                {isExceedingMaxTravelers
                  ? I18n.t('post.exceeds_max_travelers')
                  : I18n.t('generic.check_availability')}
              </Button>
              {isExceedingMaxTravelers && (
                <div className="MaxTravelersWarning">
                  <SvgIcon
                    name="warning"
                    className="warning-icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  <span>
                    {I18n.t('post.max_travelers_exceeded', {
                      max: maxCount,
                    })}
                  </span>
                </div>
              )}
              <img src="/images/stripe-logo-1.svg" className="stripe-logo" alt="Stripe Logo" />
            </div>
          </div>
        )}
      </div>
    </StickyBox>
  )
}

const StickyBox = styled.div`
  width: 34%;
  max-width: 38rem;
  min-width: 32rem;
  padding: 2.4rem 0 6.4rem;

  @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    display: none;
  }

  .PostShow_StickyBox_content {
    position: sticky;
    top: 12rem;
    width: 100%;
    padding: 2.8rem 2.4rem;
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 1.2rem;
    background-color: ${constants.COLORS.white};
    box-shadow: ${constants.COLORS.cardShadow};
    z-index: 10;

    @media (max-width: ${constants.BREAKPOINT_DESKTOP_LARGE}px) {
      padding: 1.6rem;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
      justify-content: center;
    }
  }
  .PostTop_buyButton_price {
    font-size: 1.6rem;
    line-height: 2.4rem;
    margin-bottom: 2.4rem;
    .label {
      font-size: 2rem;
      font-weight: 700;
      margin-right: 0.4rem;
    }
    .value {
      font-size: 2.4rem;
      font-weight: 700;
      margin: 0 0.4rem;
    }
    .unit {
      margin-left: 0.4rem;
    }
  }
  .LastBookedHours {
    display: flex;
    align-items: center;
    line-height: 2.4rem;
    margin: -1.6rem 0 2rem;
    font-size: 1.4rem;
    .label {
      display: flex;
      align-items: center;
      margin-right: 0.4rem;
      color: ${constants.COLORS.primary};
      font-weight: 700;
      .icon {
        margin-right: 0.4rem;
      }
    }
    .value {
      font-size: 1.6rem;
      font-weight: 700;
      margin-right: 0.4rem;
    }
  }
  .PostShow_DatePicker {
    margin-bottom: 1.6rem;
  }
  p.PostShow_PrivateGroupOption {
    line-height: 2rem;
    font-size: 1.4rem;
    margin-bottom: 2.4rem;
    a {
      color: ${constants.COLORS.black};
      text-decoration: underline;
      margin-left: 0.4rem;
      &:hover {
        text-decoration: none;
      }
    }
  }
  .PostShow_StickyBox_buyButton {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    .Button {
      width: 100%;
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background-color: ${constants.COLORS.divider || '#ccc'};
        border-color: ${constants.COLORS.divider || '#ccc'};
      }
    }
    .MaxTravelersWarning {
      display: flex;
      align-items: center;
      padding: 0.8rem 1.2rem;
      background-color: ${constants.COLORS.cautionBG || '#fff3cd'};
      border: 1px solid ${constants.COLORS.caution || '#ffeaa7'};
      border-radius: 0.8rem;
      font-size: 1.4rem;
      color: ${constants.COLORS.cautionText || '#856404'};
      margin-top: -1.2rem;
      .warning-icon {
        width: 1.6rem;
        height: 1.6rem;
        margin-right: 0.8rem;
        color: ${constants.COLORS.caution || '#856404'};
      }
    }
    .stripe-logo {
      width: auto;
      height: 2.8rem;
    }
  }
`
